import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatPrice as formatPriceUtil } from '../utils/currencyUtils'
import { useCurrency } from '../contexts/CurrencyContext'
import { optimizeProfessionalImage } from '../utils/supabaseImageUtils'
import { isServiceExcludedForProfessional, filterProfessionalsForService, withAnyProfessionalOption } from '../services/professionalService'
import { mergeCartItems, removeCartService } from '../utils/bookingCartUtils'
import defaultPfp from '../assets/default_pfp.png'

const ALL_CATEGORY = { id: '__all__', name: 'All', services: null }

const Services = ({
  categories,
  onCategoryClick,
  specialists = [],
  onConfirmBooking,
  salon,
  preSelectedService = null,
  favoriteServices = [],
  onFavoriteService = null,
  variant = 'section', // 'section' (homepage preview) | 'page' (full /services page)
  initialQuery = '',
  initialCategoryId = null,
}) => {
  const { currency } = useCurrency()
  const navigate = useNavigate()
  const isPage = variant === 'page'
  const [activeCategory, setActiveCategory] = useState(isPage ? ALL_CATEGORY : null)
  const [searchQuery, setSearchQuery] = useState(initialQuery || '')
  const categoriesScrollRef = useRef(null)
  const appliedInitialCategoryRef = useRef(false)
  const [expandedServiceId, setExpandedServiceId] = useState(null)
  const [selectedProfessionalsByService, setSelectedProfessionalsByService] = useState({})
  const [bookingSummary, setBookingSummary] = useState([])
  const [expandedCategories, setExpandedCategories] = useState({})
  const [isMobilePanelExpanded, setIsMobilePanelExpanded] = useState(false)
  const hasRestoredFromStorage = useRef(false)
  const servicesSectionRef = useRef(null)
  const servicesContainerRef = useRef(null)
  const stickyPanelRef = useRef(null)
  const [stickyPanelHeight, setStickyPanelHeight] = useState('auto')

  // Allow other parts of the app (booking modals, dashboard nav) to clear the sticky selection panel.
  const clearServicesSelection = () => {
    setBookingSummary([])
    setSelectedProfessionalsByService({})
    setExpandedServiceId(null)
    setExpandedCategories({})
    setIsMobilePanelExpanded(false)
    try {
      localStorage.removeItem('servicesBookingSummary')
    } catch (e) {
      // no-op
    }
  }

  const applyBookingSummary = (parsed) => {
    if (!Array.isArray(parsed) || parsed.length === 0) return false

    const validSummary = parsed.filter((item) => {
      if (!item?.service || !item?.professional) return false
      if (!categories.length || !specialists.length) return true

      const serviceExists = categories.some((cat) =>
        cat.services?.some((s) => s.id === item.service.id)
      )
      const professionalExists =
        item.professional.id === 'any' ||
        specialists.some((s) => s.id === item.professional.id)

      return serviceExists && professionalExists
    })

    if (validSummary.length === 0) return false

    setBookingSummary(validSummary)
    const professionalsMap = {}
    validSummary.forEach((item) => {
      if (item.service && item.professional) {
        professionalsMap[item.service.id] = item.professional
      }
    })
    setSelectedProfessionalsByService(professionalsMap)
    hasRestoredFromStorage.current = true
    return true
  }

  useEffect(() => {
    const clearHandler = () => clearServicesSelection()
    const restoreHandler = (event) => {
      const detail = event?.detail
      if (Array.isArray(detail) && detail.length > 0) {
        applyBookingSummary(detail)
        return
      }
      try {
        const raw = localStorage.getItem('servicesBookingSummary')
        if (raw) applyBookingSummary(JSON.parse(raw))
      } catch (e) {
        // no-op
      }
    }
    const mergeHandler = (event) => {
      const incoming = event?.detail
      if (!Array.isArray(incoming) || incoming.length === 0) return
      setBookingSummary((prev) => {
        const updated = mergeCartItems(prev, incoming)
        if (prev.length === 0 && updated.length > 0) {
          setIsMobilePanelExpanded(true)
        }
        return updated
      })
      setSelectedProfessionalsByService((prev) => {
        const next = { ...prev }
        incoming.forEach((item) => {
          if (item?.service?.id && item?.professional) {
            next[item.service.id] = item.professional
          }
        })
        return next
      })
      hasRestoredFromStorage.current = true
    }
    const removeHandler = (event) => {
      const serviceId = event?.detail?.serviceId
      if (serviceId == null) return
      setBookingSummary((prev) => removeCartService(prev, serviceId))
      setSelectedProfessionalsByService((prev) => {
        const next = { ...prev }
        delete next[serviceId]
        return next
      })
    }
    window.addEventListener('clearServicesBookingSummary', clearHandler)
    window.addEventListener('restoreServicesBookingSummary', restoreHandler)
    window.addEventListener('mergeServicesBookingSummary', mergeHandler)
    window.addEventListener('removeFromServicesBookingSummary', removeHandler)
    const expandCartHandler = () => {
      setIsMobilePanelExpanded(true)
      // Ensure sticky sidebar is visible after professional-first picks
      requestAnimationFrame(() => {
        document.getElementById('services')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    }
    window.addEventListener('expandServicesBookingCart', expandCartHandler)
    return () => {
      window.removeEventListener('clearServicesBookingSummary', clearHandler)
      window.removeEventListener('restoreServicesBookingSummary', restoreHandler)
      window.removeEventListener('mergeServicesBookingSummary', mergeHandler)
      window.removeEventListener('removeFromServicesBookingSummary', removeHandler)
      window.removeEventListener('expandServicesBookingCart', expandCartHandler)
    }
  }, [categories, specialists])

  // Set first category as active by default
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(isPage ? ALL_CATEGORY : categories[0])
    }
  }, [categories, activeCategory, isPage])

  useEffect(() => {
    if (!isPage || appliedInitialCategoryRef.current || !categories.length || !initialCategoryId) return
    const match = categories.find((c) => String(c.id) === String(initialCategoryId))
    if (match) {
      setActiveCategory(match)
      appliedInitialCategoryRef.current = true
    }
  }, [categories, initialCategoryId, isPage])

  useEffect(() => {
    if (typeof initialQuery === 'string' && initialQuery) {
      setSearchQuery(initialQuery)
    }
  }, [initialQuery])

  // Load booking summary from localStorage on mount and when data is ready
  useEffect(() => {
    // Only restore once and when we have categories and specialists loaded
    if (hasRestoredFromStorage.current || categories.length === 0 || specialists.length === 0) {
      return
    }

    try {
      const savedBookingSummary = localStorage.getItem('servicesBookingSummary')
      if (savedBookingSummary) {
        const parsed = JSON.parse(savedBookingSummary)
        if (Array.isArray(parsed) && parsed.length > 0) {
          if (applyBookingSummary(parsed)) {
            console.log('✅ Restored booking summary from localStorage:', parsed.length, 'services')
          } else {
            console.warn('⚠️ Saved booking summary contains invalid services/professionals, clearing...')
            localStorage.removeItem('servicesBookingSummary')
            hasRestoredFromStorage.current = true
          }
        } else {
          hasRestoredFromStorage.current = true
        }
      } else {
        hasRestoredFromStorage.current = true
      }
    } catch (error) {
      console.error('❌ Error loading booking summary from localStorage:', error)
      try {
        localStorage.removeItem('servicesBookingSummary')
      } catch (clearError) {
        console.error('Error clearing corrupted localStorage:', clearError)
      }
      hasRestoredFromStorage.current = true
    }
  }, [categories, specialists])

  // Save booking summary to localStorage whenever it changes
  useEffect(() => {
    // Don't save if we're still restoring from storage (to avoid overwriting with empty state)
    if (hasRestoredFromStorage.current) {
      if (bookingSummary.length > 0) {
        try {
          // Save complete booking summary with all necessary data
          const dataToSave = bookingSummary.map(item => ({
            service: {
              id: item.service.id,
              name: item.service.name,
              price: item.service.price,
              duration: item.service.duration,
              description: item.service.description,
              category_id: item.service.category_id,
              category: item.service.category
            },
            professional: {
              id: item.professional.id,
              name: item.professional.name,
              image: item.professional.image,
              image_url: item.professional.image_url,
              role: item.professional.role,
              specialty: item.professional.specialty,
              rating: item.professional.rating
            },
            category: item.category ? {
              id: item.category.id,
              name: item.category.name
            } : null
          }))
          localStorage.setItem('servicesBookingSummary', JSON.stringify(dataToSave))
          console.log('💾 Saved booking summary to localStorage:', bookingSummary.length, 'services')
        } catch (error) {
          console.error('❌ Error saving booking summary to localStorage:', error)
        }
      } else {
        // Clear localStorage when booking summary is empty (user cleared all selections)
        try {
          localStorage.removeItem('servicesBookingSummary')
          console.log('🗑️ Cleared booking summary from localStorage')
        } catch (error) {
          console.error('❌ Error clearing booking summary from localStorage:', error)
        }
      }
    }
  }, [bookingSummary])

  // Get services for active category (or all categories on the full page)
  // Filter out services excluded by selected professionals
  const categoryServices =
    activeCategory?.id === ALL_CATEGORY.id
      ? categories.flatMap((cat) =>
          (cat.services || []).map((service) => ({
            ...service,
            category_id: service.category_id || cat.id,
            _category: cat,
          })),
        )
      : (activeCategory?.services || []).map((service) => ({
          ...service,
          category_id: service.category_id || activeCategory?.id,
          _category: activeCategory,
        }))

  const query = searchQuery.trim().toLowerCase()
  const searchedServices = !query
    ? categoryServices
    : categoryServices.filter((service) => {
        const haystack = `${service.name || ''} ${service.description || ''} ${service._category?.name || ''}`.toLowerCase()
        return haystack.includes(query)
      })

  const allActiveServices = searchedServices.filter((service) => {
    // If no professional is selected for any service, show all services
    const selectedProfessionalIds = Object.values(selectedProfessionalsByService)
      .map(p => p?.id)
      .filter(id => id && id !== 'any')
    
    if (selectedProfessionalIds.length === 0) {
      return true
    }
    
    // Check if any selected professional has excluded this service
    const categoryId = service.category_id || service._category?.id || activeCategory?.id
    if (!categoryId || categoryId === ALL_CATEGORY.id) {
      return true
    }
    
    // Check each selected professional
    for (const professionalId of selectedProfessionalIds) {
      const professional = specialists.find(s => s.id === professionalId)
      if (professional && professional.professional_specializations) {
        const isExcluded = isServiceExcludedForProfessional(
          professional,
          categoryId,
          service.id
        )
        if (isExcluded) {
          console.log(`🚫 Service "${service.name}" (${service.id}) is excluded by professional "${professional.name}"`)
          return false
        }
      }
    }
    
    return true
  })
  
  // Homepage preview: first 3 until expanded. Full page: always show all.
  const isCategoryExpanded =
    isPage || expandedCategories[activeCategory?.id] || false
  const activeServices = isCategoryExpanded 
    ? allActiveServices 
    : allActiveServices.slice(0, 3)

  // Calculate sticky panel height to match services container (excluding Show More button)
  useEffect(() => {
    if (!servicesContainerRef.current || !stickyPanelRef.current || bookingSummary.length === 0) {
      return
    }

    const calculateHeight = () => {
      // Use requestAnimationFrame to batch layout reads and avoid forced reflows
      requestAnimationFrame(() => {
        const servicesContainer = servicesContainerRef.current
        if (!servicesContainer) return

        // Get all service cards within the container
        const serviceCards = servicesContainer.querySelectorAll('[id^="service-"]')
        
        if (serviceCards.length === 0) {
          return
        }

        // Get the last service card
        const lastServiceCard = serviceCards[serviceCards.length - 1]
        
        // Batch all layout reads together to avoid forced reflows
        const lastCardOffsetTop = lastServiceCard.offsetTop
        const lastCardOffsetHeight = lastServiceCard.offsetHeight
        const containerHeight = lastCardOffsetTop + lastCardOffsetHeight
        
        // Set the sticky panel height to match the services container (ending at last service)
        setStickyPanelHeight(`${containerHeight}px`)
      })
    }

    // Calculate after a short delay to ensure DOM is updated
    const timeoutId = setTimeout(calculateHeight, 100)
    
    // Recalculate on resize
    window.addEventListener('resize', calculateHeight)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', calculateHeight)
    }
  }, [activeCategory, expandedServiceId, expandedCategories, bookingSummary.length, activeServices.length])

  // Handle pre-selected service from favorites
  useEffect(() => {
    if (preSelectedService && categories.length > 0) {
      // Find the category that contains this service
      const serviceCategory = categories.find(cat => 
        cat.services?.some(s => s.id === preSelectedService.id)
      )
      
      if (serviceCategory) {
        // Set the category as active
        setActiveCategory(serviceCategory)
        // Expand the service
        setExpandedServiceId(preSelectedService.id)
        // Expand the category to show all services
        setExpandedCategories(prev => ({ ...prev, [serviceCategory.id]: true }))
        
        // Scroll to the service after a short delay to ensure DOM is ready
        setTimeout(() => {
          const serviceElement = document.getElementById(`service-${preSelectedService.id}`)
          if (serviceElement) {
            serviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Add a visual highlight effect
            serviceElement.classList.add('animate-pulse')
            setTimeout(() => {
              serviceElement.classList.remove('animate-pulse')
            }, 2000)
          }
        }, 500)
      }
    }
  }, [preSelectedService, categories])

  // Format price with user's selected currency
  const formatPrice = (price) => {
    return formatPriceUtil(price, currency)
  }

  // Format duration
  const formatDuration = (duration) => {
    if (!duration) return ''
    // If duration is already formatted (e.g., "15 mins"), return as is
    if (typeof duration === 'string' && duration.includes('min')) {
      return duration
    }
    // If it's a number, add "mins"
    if (typeof duration === 'number') {
      return `${duration} mins`
    }
    return duration
  }

  const handleCategoryClick = (category) => {
    setActiveCategory(category)
    
    // Scroll the clicked category into view
    if (categoriesScrollRef.current) {
      const categoryButton = categoriesScrollRef.current.querySelector(`button[data-category-id="${category.id}"]`)
      if (categoryButton) {
        categoryButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }
  }

  // Get professionals for a service based on category
  // Exclude professionals who have excluded this service
  const getProfessionalsForService = (service, category) => {
    const anyProfessional = { 
      id: 'any', 
      name: 'Any Professional', 
      role: 'Fastest match', 
      specialty: 'Fastest match',
      rating: null, 
      image: null, 
      image_url: null,
      specializations: [] 
    }

    if (!specialists || specialists.length === 0) return []

    const categoryName = category?.name || ''
    const categoryId = category?.id || service?.category_id
    const serviceId = service?.id
    const realSpecialists = specialists.filter((s) => s && s.id && s.id !== 'any')

    let eligible = []
    if (categoryId && serviceId) {
      eligible = filterProfessionalsForService(realSpecialists, categoryId, serviceId)
    }

    if (!eligible.length && categoryName) {
      eligible = realSpecialists.filter(specialist => {
        if (!specialist.specializations || !Array.isArray(specialist.specializations) || specialist.specializations.length === 0) {
          return false
        }

        const hasCategorySpecialization = specialist.specializations.some(spec => {
          if (typeof spec === 'string') {
            return spec.toLowerCase() === categoryName.toLowerCase()
          }
          if (typeof spec === 'object' && spec !== null) {
            const specName = spec.name || spec.category_name
            return specName && specName.toLowerCase() === categoryName.toLowerCase()
          }
          return false
        })

        if (!hasCategorySpecialization) return false

        if (categoryId && service?.id) {
          return !isServiceExcludedForProfessional(specialist, categoryId, service.id)
        }
        return true
      })
    }

    return withAnyProfessionalOption(eligible, anyProfessional)
  }

  useEffect(() => {
    if (!specialists?.length) return
    setBookingSummary((prev) => {
      const next = prev.filter((item) => {
        const cat =
          item.category ||
          categories.find((c) => c.services?.some((s) => s.id === item.service?.id))
        return getProfessionalsForService(item.service, cat).length > 0
      })
      return next.length === prev.length ? prev : next
    })
  }, [specialists, categories])

  const scrollServiceIntoView = (serviceId) => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const serviceElement = document.getElementById(`service-${serviceId}`)
        if (!serviceElement) return

        const serviceRect = serviceElement.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop
        const navBarHeight = 80
        const padding = 20
        const desiredTopPosition = navBarHeight + padding
        const serviceTop = serviceRect.top + currentScrollY
        const isServiceVisible =
          serviceRect.top >= desiredTopPosition && serviceRect.bottom <= viewportHeight

        if (!isServiceVisible) {
          window.scrollTo({
            top: Math.max(0, serviceTop - desiredTopPosition),
            behavior: 'smooth',
          })
        }
      })
    }, 200)
  }

  const handleServiceExpand = (service) => {
    if (expandedServiceId === service.id) {
      setExpandedServiceId(null)
      return
    }

    setExpandedServiceId(service.id)
    scrollServiceIntoView(service.id)
  }

  const handleBookClick = (service) => {
    handleServiceExpand(service)
  }

  const handleProfessionalSelect = (service, professional, category) => {
    if (!professional || getProfessionalsForService(service, category).length === 0) return
    // Automatically add to booking summary when professional is selected
    // Ensure professional object has all necessary fields including image
    const professionalWithImage = {
      ...professional,
      image: professional.image || professional.image_url || null,
      image_url: professional.image_url || professional.image || null,
      role: professional.role || professional.specialty || null,
      specialty: professional.specialty || professional.role || null
    }
    
    const bookingItem = {
      service: {
        ...service,
        category: category
      },
      professional: professionalWithImage,
      category: category
    }

    setBookingSummary(prev => {
      // Remove if already exists, then add new one
      const filtered = prev.filter(item => item.service.id !== service.id)
      const updated = [...filtered, bookingItem]
      // Auto-expand mobile panel when first service is added
      if (prev.length === 0 && updated.length === 1) {
        setIsMobilePanelExpanded(true)
      }
      return updated
    })

    setSelectedProfessionalsByService(prev => ({
      ...prev,
      [service.id]: professional
    }))

    // Collapse the panel after selection
    setExpandedServiceId(null)
  }

  const isProfessionalSelected = (serviceId, professionalId) => {
    return selectedProfessionalsByService[serviceId]?.id === professionalId
  }


  const handleRemoveFromBooking = (serviceId) => {
    setBookingSummary(prev => prev.filter(item => item.service.id !== serviceId))
    setSelectedProfessionalsByService(prev => {
      const updated = { ...prev }
      delete updated[serviceId]
      return updated
    })
  }

  const handleProceedToBooking = () => {
    if (bookingSummary.length === 0) {
      alert('Please add at least one service to booking')
      return
    }

    // Call parent callback with booking data
    if (onConfirmBooking) {
      onConfirmBooking(bookingSummary)
    }
  }

  const calculateTotal = () => {
    return bookingSummary.reduce((sum, item) => {
      const price = typeof item.service.price === 'string' ? parseFloat(item.service.price) : item.service.price || 0
      return sum + price
    }, 0)
  }

  const scrollCategories = (direction) => {
    if (categoriesScrollRef.current) {
      const scrollAmount = 200
      categoriesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <React.Fragment>
    <section id="services" className={`${isPage ? 'py-6 sm:py-8' : 'py-4 sm:py-6 lg:py-8'} px-2 sm:px-4 md:px-6 lg:px-8 ${bookingSummary.length > 0 ? 'pb-32 lg:pb-20' : ''}`}>
      <div className={`max-w-7xl mx-auto ${bookingSummary.length > 0 ? 'lg:grid lg:grid-cols-3 lg:gap-6' : ''}`}>
        {/* Services Heading */}
        <div ref={servicesSectionRef} className={bookingSummary.length > 0 ? 'lg:col-span-2' : ''}>
          <div className={`mb-3 sm:mb-4 ${isPage ? 'flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3' : 'text-center'}`}>
            <div className={isPage ? 'text-left' : ''}>
              <h2 className={`font-bold text-gray-900 ${isPage ? 'text-2xl sm:text-3xl' : 'text-2xl sm:text-3xl lg:text-4xl'}`}>
                {isPage ? 'Services' : 'Our Services'}
              </h2>
              {isPage ? (
                <p className="text-sm text-gray-500 mt-1">Browse all salon services and book your appointment.</p>
              ) : null}
            </div>
            {!isPage && categories.length > 0 ? (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/services')}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-purple-700 transition"
                >
                  See all services
                  <i className="fas fa-arrow-right text-xs" />
                </button>
              </div>
            ) : null}
          </div>
        
        {categories.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
              <i className="fas fa-spa text-3xl text-purple-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Services Available Yet</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Services will appear here once they are added through the admin panel.
            </p>
          </div>
        ) : (
          <>
            {isPage ? (
              <div className="relative mb-4">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search services..."
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            ) : null}

            {/* Category Filters/Tabs - Text Based, No Images */}
            <div className="relative mb-4 sm:mb-5">
              {/* Left Navigation Arrow */}
              <div className="absolute left-0 top-0 bottom-0 flex items-center bg-gradient-to-r from-white via-white to-transparent pr-4 pointer-events-none z-10">
                <button
                  onClick={() => scrollCategories('left')}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-50 hover:text-primary text-gray-700 transition-all pointer-events-auto"
                  aria-label="Scroll left"
                >
                  <i className="fas fa-angle-left text-lg"></i>
                </button>
              </div>

              {/* Category Buttons Container */}
              <div 
                className="flex items-center gap-4 overflow-x-auto [&::-webkit-scrollbar]:hidden px-12" 
                ref={categoriesScrollRef}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {(isPage ? [ALL_CATEGORY, ...categories] : categories).map((category) => {
                  const isActive = activeCategory?.id === category.id
                  return (
                    <button
                      key={category.id}
                      data-category-id={category.id}
                      onClick={() => handleCategoryClick(category)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm sm:text-base whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-black text-white'
                          : 'text-gray-700 hover:text-primary'
                      }`}
                    >
                      {category.name}
                    </button>
                  )
                })}
              </div>
              
              {/* Right Navigation Arrow */}
              <div className="absolute right-0 top-0 bottom-0 flex items-center bg-gradient-to-l from-white via-white to-transparent pl-4 pointer-events-none z-10">
                <button
                  onClick={() => scrollCategories('right')}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-50 hover:text-primary text-gray-700 transition-all pointer-events-auto"
                  aria-label="Scroll right"
                >
                  <i className="fas fa-angle-right text-lg"></i>
                </button>
              </div>
            </div>

            {/* Services List - Vertical Cards */}
            {allActiveServices.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No services available in this category.</p>
              </div>
            ) : (
              <>
                <div ref={servicesContainerRef} className="space-y-2 sm:space-y-3">
                  {activeServices.map((service) => {
                  const serviceCategory = service._category || activeCategory
                  const isExpanded = expandedServiceId === service.id
                  const isInBooking = bookingSummary.some(item => item.service.id === service.id)
                  const isPreSelected = preSelectedService && preSelectedService.id === service.id
                  const selectedProfessional = selectedProfessionalsByService[service.id]
                  const professionals = getProfessionalsForService(service, serviceCategory)
                  const isUnstaffed = professionals.length === 0
                  const salonName = salon?.name || 'this location'
                  
                  return (
                    <div 
                      key={service.id} 
                      id={`service-${service.id}`}
                      className={`bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow ${
                        isPreSelected ? 'border-primary border-2 shadow-lg ring-2 ring-primary ring-opacity-20' : 'border-gray-200'
                      }`}
                    >
                      {/* Service Card — tap row to expand professionals */}
                      <div
                        className="p-3 sm:p-4 flex items-center justify-between cursor-pointer"
                        onClick={() => handleServiceExpand(service)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleServiceExpand(service)
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="mb-0.5">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900">
                            {service.name}
                          </h3>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500 mb-1">
                            {formatDuration(service.duration)}
                          </p>
                          {service.description && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1 line-clamp-1">
                              {service.description}
                            </p>
                          )}
                          <p className="text-base sm:text-lg font-bold text-primary">
                            {formatPrice(service.price)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 ml-2 sm:ml-4">
                          {onFavoriteService && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onFavoriteService(service.id)
                              }}
                              className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition hover:bg-gray-100 z-10 relative"
                              aria-label={favoriteServices.includes(service.id) ? 'Remove from favorites' : 'Add to favorites'}
                              type="button"
                            >
                              <i className={`${
                                favoriteServices.includes(service.id)
                                  ? 'fas fa-heart text-red-500'
                                  : 'far fa-heart text-gray-400 hover:text-red-500 transition duration-300 hover:scale-110 hover:rotate-12'
                              } text-sm sm:text-base transition`}></i>
                            </button>
                          )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleBookClick(service)
                          }}
                            className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg transition text-xs sm:text-sm whitespace-nowrap font-medium ${
                            isUnstaffed
                              ? 'bg-gray-100 text-gray-500 cursor-pointer'
                              : isInBooking
                              ? 'bg-gray-800 text-white'
                              : 'bg-secondary text-white hover:bg-gray-800'
                          }`}
                        >
                          {isUnstaffed ? 'Unavailable' : isInBooking ? 'Selected' : 'Book'}
                        </button>
                        </div>
                      </div>

                      {/* Slide-down Professional Selection Panel */}
                      {isExpanded && (
                        <div 
                          id={`professional-panel-${service.id}`}
                          className="border-t border-gray-200 px-4 sm:px-6 py-4 sm:py-6 animate-slideDown overflow-hidden"
                        >
                          {isUnstaffed ? (
                            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-center">
                              <p className="text-sm font-semibold text-gray-900 mb-1">Not available to book</p>
                              <p className="text-sm text-gray-600">
                                No specialist is assigned to this service at {salonName} yet. Choose another service, or contact the salon.
                              </p>
                            </div>
                          ) : (
                            <>
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                            Select Professional
                          </h4>
                              <div className="flex gap-4 overflow-x-auto pb-4 mb-4 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                {professionals.map((professional) => {
                                  const isSelected = isProfessionalSelected(service.id, professional.id)
                                  const isAnyProfessional = professional.id === 'any'
                                  
            return (
            <div
                                      key={professional.id}
                                      onClick={() => handleProfessionalSelect(service, professional, serviceCategory)}
                                      className={`flex-shrink-0 w-32 sm:w-40 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        isSelected
                                          ? 'border-black bg-gray-50'
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      {isAnyProfessional ? (
                                        <>
                                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-2 bg-gray-200 flex items-center justify-center">
                                            <i className="fas fa-users text-2xl text-gray-600"></i>
                                          </div>
                                          <h5 className="text-sm font-semibold text-center text-gray-900 mb-1">
                                            {professional.name}
                                          </h5>
                                          <p className="text-xs text-center text-gray-500 mb-2">
                                            {professional.role}
                                          </p>
                                        </>
                                      ) : (
                                        <>
                                       <img 
                                            src={professional.image_url 
                                              ? optimizeProfessionalImage(professional.image_url, 'small')
                                              : (professional.image || defaultPfp)}
                                            alt={professional.name}
                                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-2 object-cover"
                                            loading="lazy"
                                            width="80"
                                            height="80"
                                            onError={(e) => {
                                              e.target.style.display = 'none'
                                              e.target.nextSibling.style.display = 'flex'
                                            }}
                                          />
                                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-2 bg-gray-200 flex items-center justify-center hidden">
                                            <i className="fas fa-user text-2xl text-gray-600"></i>
                                          </div>
                                          <h5 className="text-sm font-semibold text-center text-gray-900 mb-1">
                                            {professional.name}
                                          </h5>
                                          <p className="text-xs text-center text-gray-600 mb-2">
                                            {professional.role || professional.specialty}
                                          </p>
                                          <div className="flex items-center justify-center gap-1">
                                            <i className="fas fa-star text-yellow-400 text-xs"></i>
                                            <span className="text-xs text-gray-600">
                                              {Number(professional.rating || 0).toFixed(1)}
                                            </span>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
                </div>

                {/* See all category — homepage preview only (opens /services like mobile) */}
                {!isPage && allActiveServices.length > 3 && activeCategory && activeCategory.id !== ALL_CATEGORY.id && (
                  <div className="mt-3 sm:mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/services', {
                          state: { categoryId: activeCategory.id, categoryName: activeCategory.name },
                        })
                      }}
                      className="flex items-center gap-2 text-gray-700 hover:text-primary transition font-medium text-xs sm:text-sm"
                    >
                      <span>See all {activeCategory.name}</span>
                      <i className="fas fa-chevron-right text-[10px]"></i>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Booking Summary - Removed inline, now shown in floating widget */}
          </>
        )}
        </div>

        {/* Right Side Panel - Booking Summary (Desktop) */}
        {bookingSummary.length > 0 && (
          <div className="hidden lg:block lg:col-span-1 lg:pt-[118px] animate-slideInRight">
          <div 
            ref={stickyPanelRef}
            className="sticky top-24 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 z-40 flex flex-col overflow-hidden"
            style={{ maxHeight: stickyPanelHeight }}
          >
            {/* Salon Information */}
            {salon && (
              <div className="flex items-start gap-3 mb-4 pb-4 border-b flex-shrink-0">
                {salon.image_urls && salon.image_urls.length > 0 ? (
                  <img
                    src={salon.image_urls[0]}
                    alt={salon.name}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-spa text-2xl text-purple-400"></i>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-base text-gray-900 mb-1 truncate">
                    {salon.name || 'Salon'}
                  </h3>
                  {salon.rating > 0 && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${
                              i < Math.floor(salon.rating)
                                ? 'fas fa-star'
                                : 'far fa-star'
                            }`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-xs text-gray-600">
                        {salon.rating.toFixed(1)}
                      </span>
                      {salon.total_reviews && (
                        <span className="text-xs text-gray-500">
                          ({salon.total_reviews})
                        </span>
                      )}
                    </div>
                  )}
                  {salon.location && (
                    <p className="text-xs text-gray-600 truncate">
                      {salon.location}
                  </p>
                  )}
                </div>
              </div>
            )}

            {/* Selected Services — scroll only after 5 items */}
            <div 
              className="mb-4 pb-4 border-b pr-2 flex-1 min-h-0"
              style={bookingSummary.length > 5 ? { 
                maxHeight: '300px',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch'
              } : {
                overflowY: 'hidden',
                maxHeight: 'none'
              }}
            >
              <h4 className="font-semibold text-sm text-gray-900 mb-2 flex-shrink-0">
                Selected Services
              </h4>
              <div className="space-y-2">
                {bookingSummary.map((item) => (
                  <div key={item.service.id} className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {item.service.name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {formatDuration(item.service.duration)} with {item.professional.name}
                </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatPrice(item.service.price)}
                      </span>
                      <button
                        onClick={() => handleRemoveFromBooking(item.service.id)}
                        className="text-red-500 hover:text-red-700 transition"
                        aria-label="Remove service"
                        title="Remove service"
                      >
                        <i className="fas fa-times text-xs"></i>
                </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="mb-4 pb-4 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-gray-900">Total</span>
                <span className="text-lg font-bold text-primary">
                  {formatPrice(calculateTotal())}
                </span>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={handleProceedToBooking}
              className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition text-sm font-semibold flex-shrink-0"
            >
              Continue
            </button>
          </div>
        </div>
        )}

        {/* Mobile Sticky Bottom Panel - Booking Summary */}
        {bookingSummary.length > 0 && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 animate-gentleBounce">
            <div className="max-w-full mx-auto">
              {/* Collapsible Header - Clickable to expand/collapse */}
              <div 
                className="px-4 py-3 border-b border-gray-200 cursor-pointer transition-all duration-200 hover:bg-gray-50"
                onClick={() => setIsMobilePanelExpanded(!isMobilePanelExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 transition-transform duration-200 hover:scale-110">
                      <span className="text-white text-sm font-bold">{bookingSummary.length}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Booking Summary</p>
                      <p className="text-xs text-gray-600">{formatPrice(calculateTotal())}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsMobilePanelExpanded(!isMobilePanelExpanded)
                      }}
                      className="text-gray-500 hover:text-gray-700 transition-all duration-300 flex-shrink-0 transform"
                      aria-label={isMobilePanelExpanded ? "Collapse" : "Expand"}
                    >
                      <i className={`fas fa-chevron-${isMobilePanelExpanded ? 'down' : 'up'} text-sm transition-transform duration-300 ${isMobilePanelExpanded ? 'rotate-180' : ''}`}></i>
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleProceedToBooking()
                    }}
                    className="bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-all duration-200 ml-2 flex-shrink-0 transform hover:scale-105 active:scale-95"
                  >
                    Continue
                  </button>
                </div>
              </div>

              {/* Expandable Content */}
              <div 
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isMobilePanelExpanded 
                    ? 'max-h-[60vh] opacity-100' 
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-4 py-3">
                  {/* Selected Services List */}
                  <div className="space-y-3 mb-3">
                    {bookingSummary.map((item) => (
                      <div 
                        key={item.service.id} 
                        className={`flex items-start justify-between gap-2 bg-gray-50 rounded-lg p-2 transition-all duration-300 ${
                          isMobilePanelExpanded 
                            ? 'opacity-100 transform translate-y-0' 
                            : 'opacity-0 transform -translate-y-2'
                        }`}
                        style={{ transitionDelay: `${bookingSummary.indexOf(item) * 50}ms` }}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 mb-0.5">
                            {item.service.name}
                          </p>
                          <p className="text-xs text-gray-600">
                            {formatDuration(item.service.duration)} • {item.professional.name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs font-semibold text-gray-900">
                            {formatPrice(item.service.price)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveFromBooking(item.service.id)
                            }}
                            className="text-red-500 hover:text-red-700 transition-all duration-200 p-1 hover:scale-110 active:scale-95"
                            aria-label="Remove service"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div 
                    className={`flex items-center justify-between pt-2 border-t border-gray-200 transition-all duration-300 ${
                      isMobilePanelExpanded 
                        ? 'opacity-100 transform translate-y-0' 
                        : 'opacity-0 transform translate-y-2'
                    }`}
                    style={{ transitionDelay: `${bookingSummary.length * 50}ms` }}
                  >
                    <span className="text-sm font-semibold text-gray-900">Total</span>
                    <span className="text-base font-bold text-primary">
                      {formatPrice(calculateTotal())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
    </React.Fragment>
  )
}

export default Services
